import React from 'react';
import { S39AdBanner } from '@milanlaser/mlcomponents';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { useBuilderImages } from '../../context/BuilderImageContext';
import { useOptimizedImage } from '../../../../hooks/useOptimizedImage';
import { createImageRenderer } from '../../../../utils/general/renderOptimizedImage';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state?: BuilderState<T>;
    };
};

// Omit the padding props from the original S39AdBanner component
// and re-add them with our Builder-specific PaddingSize type
type S39AdBannerArgs = BuilderProps<{
    model: string;
}> &
    Omit<
        React.ComponentProps<typeof S39AdBanner>,
        'desktopPaddingTop' | 'desktopPaddingBottom' | 'mobilePaddingTop' | 'mobilePaddingBottom'
    > & {
        desktopPaddingTop?: PaddingSize;
        desktopPaddingBottom?: PaddingSize;
        mobilePaddingTop?: PaddingSize;
        mobilePaddingBottom?: PaddingSize;
    };

export const S39AdBannerSection = (props: S39AdBannerArgs) => {
    const { getOptimizedImage } = useBuilderImages();
    const {
        desktopPaddingTop = 'none',
        desktopPaddingBottom = 'none',
        mobilePaddingTop = 'none',
        mobilePaddingBottom = 'none',
        builderState,
        ...restProps
    } = props;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    // Get optimized versions of mobile and desktop images
    const mobileImageInfo = props.mobileImageSrc ? getOptimizedImage(props.mobileImageSrc) : null;
    const desktopImageInfo = props.desktopImageSrc ? getOptimizedImage(props.desktopImageSrc) : null;

    const { gatsbyImageData: mobileGatsbyImageData, altText: mobileAltText } = useOptimizedImage(
        mobileImageInfo?.nodeId || '',
        mobileImageInfo?.altText || props.mobileImageAlt || 'Mobile Ad Banner'
    );

    const { gatsbyImageData: desktopGatsbyImageData, altText: desktopAltText } = useOptimizedImage(
        desktopImageInfo?.nodeId || '',
        desktopImageInfo?.altText || props.desktopImageAlt || 'Desktop Ad Banner'
    );

    const renderMobileImage = createImageRenderer({
        optimizedImageData: mobileGatsbyImageData,
        optimizedAlt: mobileAltText,
        shouldUseOptimized: () => !!mobileImageInfo && !!props.mobileImageSrc,
    });

    const renderDesktopImage = createImageRenderer({
        optimizedImageData: desktopGatsbyImageData,
        optimizedAlt: desktopAltText,
        shouldUseOptimized: () => !!desktopImageInfo && !!props.desktopImageSrc,
    });

    return (
        <S39AdBanner
            {...restProps}
            className={`mcl-mx-auto ${topPaddingClasses} ${bottomPaddingClasses}`}
            renderMobileImage={renderMobileImage}
            renderDesktopImage={renderDesktopImage}
        />
    );
};
